import { useState, useEffect } from "react";
import { Field, Label, Switch, Description, Input } from "@headlessui/react";
import { SelectListItem } from "../../components/input/SelectList";
import KeyValuePair from "../../interfaces/keyValuePair";
import MultiSelect from "../peerReviewGroups/MultiSelect";
import { AdvancedSettingsViewModel } from "./promptsApiSlice";

interface AssignmentStepProps {
	model: AdvancedSettingsViewModel;
	availableUsers: SelectListItem[];
	availableReminders: SelectListItem[];
	setAdvancedModel: (model: AdvancedSettingsViewModel) => void;
	setHasError: (hasError: boolean) => void;
}

export default function AssignmentStep(props: AssignmentStepProps) {
	const { model, availableUsers, availableReminders, setAdvancedModel, setHasError } = props;
	const dateNow = new Date().toISOString().slice(0, 16);

	const initializeAssignedUsers = () => {
		const selected = availableUsers
			.filter((s) => model.assignedToIds?.includes(Number(s.value)))
			.map((s) => ({ key: Number(s.value), value: s.text }));
		return selected;
	};

	const initializeReminders = () => {
		const selected = availableReminders
			.filter((s) => model.selectedReminders.includes(Number(s.value)))
			.map((s) => ({ key: Number(s.value), value: s.text }));
		return selected;
	};

	const availableAssignedUsersPool = availableUsers.map((x) => ({
		key: Number(x.value),
		value: x.text,
	}));

	const availableRemindersPool = availableReminders.map((x) => ({
		key: Number(x.value),
		value: x.text,
	}));

	const [selectedAssignedUsers, setSelectedAssignedUsers] = useState(initializeAssignedUsers());
	const [selectedReminders, setSelectedReminders] = useState(initializeReminders());
	const [dueDateEnabled, setDueDateEnabled] = useState(model.dueDateEnabled);
	const [dueDateRequired, setDueDateRequired] = useState(model.dueDateRequired);
	const [dueDate, setDueDate] = useState(model.dueDate.toLocaleString());

	function handleSelectedAssignedUsersChange(selected: KeyValuePair<number, string>[]) {
		setSelectedAssignedUsers(selected);
	}

	function handleSelectedRemindersChange(selected: KeyValuePair<number, string>[]) {
		setSelectedReminders(selected);
	}

	useEffect(() => {
		setAdvancedModel(updateModel());
		if (dueDateEnabled && dueDate < dateNow) {
			setHasError(true);
		} else {
			setHasError(false);
		}
	}, [selectedAssignedUsers, dueDateEnabled, dueDateRequired, dueDate, selectedReminders]);

	function updateModel(): AdvancedSettingsViewModel {
		const advancedModel: AdvancedSettingsViewModel = {
			...model,
			assignedToIds: selectedAssignedUsers.map((u) => u.key),
			dueDateEnabled: dueDateEnabled,
			dueDateRequired: dueDateRequired,
			dueDate: dueDate,
			selectedReminders: selectedReminders.map((r) => r.key),
		};
		return advancedModel;
	}

	return (
		<section className="space-y-6">
			<div className="flex border-b border-gray-900/10 pb-4">
				<div className="w-full md:w-1/4 pr-3">
					<h2 className="leading-7">Assign Prompt</h2>
					<p className="mt-1 text-sm leading-6 text-gray-600">
						If a prompt is assigned to any students, it will only be visible to those
						students.
					</p>
				</div>
				<Field className="flex flex-col w-full md:w-3/4">
					<Label as="span" className="block font-medium leading-6">
						Assign To
					</Label>
					<MultiSelect
						pool={availableAssignedUsersPool}
						selected={selectedAssignedUsers}
						className="w-full mt-2"
						defaultText="Choose a student..."
						onSelectedChange={handleSelectedAssignedUsersChange}
					/>
				</Field>
			</div>
			<div className="flex pb-4">
				<div className="w-full md:w-1/4 pr-3">
					<h2 className="leading-7">Due Date</h2>
					<p className="mt-1 text-sm leading-6 text-gray-600">
						A prompt with a due date is treated as an assignment rather than practice.
					</p>
				</div>
				<div className="flex flex-col w-full md:w-3/4 gap-y-4">
					<Field className="flex items-center justify-between">
						<Label as="span" className="block font-medium leading-6">
							Enable Due Date
						</Label>
						<Switch
							checked={dueDateEnabled}
							onChange={setDueDateEnabled}
							className="group relative inline-flex h-6 w-11 shrink-0 cursor-pointer rounded-full border-2 border-transparent bg-gray-200 transition-colors duration-200 ease-in-out focus:outline-hidden focus:ring-2 focus:ring-primary-600 focus:ring-offset-2 data-[checked]:bg-primary-600">
							<span
								aria-hidden="true"
								className="pointer-events-none inline-block size-5 transform rounded-full bg-white shadow-sm ring-0 transition duration-200 ease-in-out group-data-[checked]:translate-x-5"
							/>
						</Switch>
					</Field>
					{dueDateEnabled && (
						<div className="flex flex-col w-full gap-y-4">
							<Field className="flex items-center justify-between">
								<span className="flex flex-col">
									<Label as="span" className="block font-medium leading-6">
										Enforce Due Date
									</Label>
									<Description
										as="span"
										className="text-sm leading-6 text-gray-600 pr-3">
										When enforced, students will not be able to submit their
										essay after the due date.
									</Description>
								</span>
								<Switch
									checked={dueDateRequired}
									onChange={setDueDateRequired}
									className="group relative inline-flex h-6 w-11 shrink-0 cursor-pointer rounded-full border-2 border-transparent bg-gray-200 transition-colors duration-200 ease-in-out focus:outline-hidden focus:ring-2 focus:ring-primary-600 focus:ring-offset-2 data-[checked]:bg-primary-600">
									<span
										aria-hidden="true"
										className="pointer-events-none inline-block size-5 transform rounded-full bg-white shadow-sm ring-0 transition duration-200 ease-in-out group-data-[checked]:translate-x-5"
									/>
								</Switch>
							</Field>
							<Field>
								<Label as="span" className="block font-medium leading-6">
									Due Date
								</Label>
								<Description className="text-sm leading-6 text-gray-600">
									Select the day and time:
								</Description>
								<Input
									type="datetime-local"
									name="dueDate"
									id="dueDate"
									className={`${dueDate < dateNow ? "focus:ring-red-600 ring-red-500" : "focus:ring-primary-600 ring-gray-300"} block w-full rounded-lg border-0 text-sm ring-1 ring-inset focus:ring-2 focus:ring-inset`}
									value={dueDate}
									min={dateNow}
									onChange={(e) => setDueDate(e.target.value)}
								/>
							</Field>
							<Field>
								<Label as="span" className="block font-medium leading-6">
									Due Date Reminders
								</Label>
								<Description className="text-sm leading-6 text-gray-600">
									Set reminder(s) to notify students before the due date:
								</Description>
								<MultiSelect
									pool={availableRemindersPool}
									selected={selectedReminders}
									className="w-full mt-2"
									defaultText="Choose a reminder..."
									onSelectedChange={handleSelectedRemindersChange}
								/>
							</Field>
						</div>
					)}
				</div>
			</div>
		</section>
	);
}
